import moment from 'moment';

import { DataConverter, ConfigHelper } from 'services/charting';

const warnChartIcon = '/assets/images/warn-chart-icon-16.png'; // eslint-disable-line
const alertChartIcon = '/assets/images/alert-chart-icon-16.png';

export default class ThingChartController {
    /** @ngInject */
    constructor(ThingsApi, NotificationsApi, $q) {
        this.ThingsApi = ThingsApi;
        this.NotificationsApi = NotificationsApi;
        this.$q = $q;

        this.extractChartData = this.extractChartData.bind(this);
        this.updateData = this.updateData.bind(this);
    }

    loadNavigatorData(start, end, interval = '1d') {
        return this.ThingsApi.historicalQueryAggregated(this.thing.id, {
            fields: this.thing.getHistoryFieldsNavigatorConfig(),
            time_period: {
                start,
                end
            },
            interval,
            fill: 'null'
        });
    }

    loadData(start, end, interval = '1d') {
        if (this.thing.isHistoryAggregated()) {
            return this.ThingsApi.historicalQueryAggregated(this.thing.id, {
                fields: this.thing.getHistoryFieldsConfig(),
                time_period: {
                    start,
                    end
                },
                interval,
                fill: 'null'
            });
        }

        return this.ThingsApi.historicalQuery(this.thing.id, {
            fields: this.thing.getHistoryFieldsConfig(),
            time_period: {
                start,
                end
            }
        });
    }

    updateData({ min, max, trigger }) {
        if (!trigger) {
            return;
        }

        const interval = DataConverter.calculateInterval(min, max);

        const start = moment(min).format();
        const end = moment(max).format();

        this.chart.showLoading('Loading data from server...');
        this.loadData(start, end, interval)
            .then(this.extractChartData)
            .then((data) => {
                this.chart.hideLoading();
                if (this.chartType === 'temperature') {
                    const { ranges, average } = data;
                    this.chart.series[0].setData(average);
                    this.chart.series[1].setData(ranges);
                } else {
                    this.chart.series[0].setData(data);
                }
            });
    }

    extractChartData(response) {
        const data = (response && response.values) ? response.values : [];
        const chartData = this.convertChartData(data);
        this.chartStats = this.calculateChartStats(chartData);
        return chartData;
    }

    calculateChartStats(data) {
        if (this.chartType === 'temperature') {
            return DataConverter.temperatureStats(data);
        } else if (this.chartType === 'touch') {
            return DataConverter.touchStats(data);
        }
        return DataConverter.proximityStats(data);
    }

    convertNavigatorData(data) {
        if (this.chartType === 'proximity') {
            return DataConverter.proximityNavigator(data);
        } else if (this.chartType === 'temperature') {
            return DataConverter.temperature(data).average;
        }
        return this.convertChartData(data);
    }

    convertChartData(data) {
        if (this.chartType === 'temperature') {
            return DataConverter.temperature(data);
        } else if (this.chartType === 'touch') {
            return DataConverter.touch(data);
        }
        return DataConverter.proximity(data);
    }

    prepareTemperatureConfig(data) {
        this.chartConfig = ConfigHelper.temperatureFull(
            data,
            ConfigHelper.asyncDataLoading(this.navigatorSeriesData, this.updateData)
        );
    }

    prepareTouchConfig(data) {
        this.chartConfig = ConfigHelper.touchFull(
            data,
            ConfigHelper.asyncDataLoading(this.navigatorSeriesData, this.updateData)
        );
    }

    prepareProximityConfig(data) {
        this.chartConfig = ConfigHelper.proximityFull(
            data,
            ConfigHelper.asyncDataLoading(this.navigatorSeriesData, this.updateData)
        );
    }

    onChartLoaded({ chart }) {
        this.chart = chart;
        this.chart.xAxis[0].setExtremes(
            this.defaultStart.valueOf(),
            this.periodEnd.valueOf()
        );
    }

    mergeNotifications() {
        if (this.notifications) {
            const notifications = this.notifications;
            const series = {
                type: 'scatter',
                name: 'Notifications',
                tooltip: {
                    split: true,
                    pointFormatter: function () { // eslint-disable-line
                        return `<h1>${notifications[this.index].subject}</h1><br />
                            <p>${notifications[this.index].message}</p>`;
                    }
                },
                zIndex: 50,
                data: []
            };
            const notification = this.notifications.shift();
            series.data.push({
                x: moment(notification.created).valueOf(),
                y: 24.5,
                marker: {
                    symbol: notification.level === 'ALERT' ? `url(${alertChartIcon})` : `url(${alertChartIcon})`
                }
            });
            this.chartConfig.series.push(series);
        }
    }

    prepareChartConfig(data) {
        if (this.chartType === 'temperature') {
            this.prepareTemperatureConfig(data);
        } else if (this.chartType === 'touch') {
            this.prepareTouchConfig(data);
        } else {
            this.prepareProximityConfig(data);
        }
        this.mergeNotifications();
        this.dataLoaded = true;
    }

    loadHistoricalData() {
        const end = this.periodEnd.format();
        const start = this.periodStart.format();

        return this.loadNavigatorData(start, end)
            .then((response) => {
                const data = (response && response.values) ? response.values : [];
                this.navigatorSeriesData = this.convertNavigatorData(data);
                return this.loadData(
                    this.defaultStart.format(),
                    end,
                    DataConverter.calculateInterval(this.defaultStart.valueOf(), this.periodEnd.valueOf())
                )
                    .then(this.extractChartData);
            });
    }

    loadNotifications() {
        return this.NotificationsApi.getByObjectId(this.selectedThingId).then((data) => {
            this.notifications = data.notifications || [];
        });
    }

    $onChanges(changes) {
        if (changes.eventReceived && !changes.eventReceived.isFirstChange()) {
            const extremes = this.chart.xAxis[0].getExtremes();
            this.updateData({
                min: extremes.min,
                max: extremes.max,
                trigger: true
            });
        }
    }

    $onInit() {
        this.chartData = null;
        this.chartType = null;
        this.dataLoaded = false;

        const { start, end } = ConfigHelper.getDefaultTimespan();

        this.periodEnd = end.endOf('day');
        this.periodStart = moment(this.thing.registered);

        this.defaultStart = start;

        this.chartType = this.thing.getHistoryChartType();
        this.$q.all([
            this.loadHistoricalData(),
            // this.loadNotifications()
        ]).then((resolved) => {
            this.prepareChartConfig(resolved[0]);
        });
    }
}



// WEBPACK FOOTER //
// ./app/components/sensors-and-connectors/components/thing-chart/thing-chart.controller.js